import type { CaptionCue } from "./caption.js";
import { timelineDuration } from "./layout.js";
import { VOICE_ROLES } from "./language-source.js";
import { languageByCode, type Language, type LanguageDepth } from "./languages.js";
import { spokenText } from "./speech.js";
import { cuesFromLines, type Alignment, type TimedLine } from "./subtitles.js";
import type { Timeline } from "./timeline.js";

/**
 * A language seen in the editor: the English timeline with the language's
 * voice, subtitles and — for the whole video — its words on screen. Pure and
 * browser-safe, so the preview and the browser export build exactly the same
 * thing. The picture (cuts, timing, zooms, layout) is English's and is never
 * changed here.
 */

export interface ScreenText {
  id: string;
  kind: "card" | "callout";
  english: string;
  limit: number;
}

export interface TranslatedText {
  id: string;
  kind: "card" | "callout";
  english: string;
  text: string;
  edited: boolean;
  runsLong: boolean;
}

/**
 * The stored screen texts that can be read, in order. An entry missing its id,
 * kind, English or text is left out.
 */
export function readTexts(value: unknown): TranslatedText[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry): TranslatedText[] => {
    const raw = entry as Record<string, unknown> | null;
    if (!raw || typeof raw.id !== "string" || typeof raw.english !== "string" || typeof raw.text !== "string") {
      return [];
    }
    if (raw.kind !== "card" && raw.kind !== "callout") return [];
    return [
      {
        id: raw.id,
        kind: raw.kind,
        english: raw.english,
        text: raw.text,
        edited: raw.edited === true,
        runsLong: raw.runsLong === true,
      },
    ];
  });
}

export interface TimelineLanguageLine {
  id: string;
  text: string;
  startAt: number;
  seconds: number;
  clipKey?: string;
  clipSeconds?: number;
  alignment?: Alignment | null;
}

const CARD_MAX = 500;
const TEXT_BOOKEND_MAX = 600;
const LABEL_MAX = 280;

/**
 * The most a stored piece of screen text can hold: the maximum of the
 * timeline field it is drawn from. A text bookend (`head:text`, `end:text`)
 * holds 600, a card's text element 500, a callout label 280. A translation
 * past its limit is kept and flagged; only past this is it cut.
 */
export function screenTextMaximum(piece: { id: string; kind: "card" | "callout" }): number {
  if (piece.kind === "callout") return LABEL_MAX;
  return piece.id === "head:text" || piece.id === "end:text" ? TEXT_BOOKEND_MAX : CARD_MAX;
}

/** How long a translated piece of screen text may run, from its English. */
export function textLimit(english: string, language: Language, max: number): number {
  const factor = language.unit === "characters" ? 0.6 : 1.3;
  return Math.max(1, Math.min(max, Math.ceil([...english].length * factor)));
}

/** Every piece of text Veyso draws on this video, in a stable order, with the id a translation is stored under. */
export function screenTexts(timeline: Timeline, language: Language): ScreenText[] {
  const out: ScreenText[] = [];
  for (const which of ["head", "end"] as const) {
    const content = timeline.sections[which]?.content;
    if (!content) continue;
    if (content.kind === "card") {
      for (const element of content.elements) {
        if (element.kind !== "text" || !element.text.trim()) continue;
        out.push({
          id: `${which}:${element.id}`,
          kind: "card",
          english: element.text,
          limit: textLimit(element.text, language, CARD_MAX),
        });
      }
    } else if (content.kind === "text" && content.text.trim()) {
      out.push({
        id: `${which}:text`,
        kind: "card",
        english: content.text,
        limit: textLimit(content.text, language, TEXT_BOOKEND_MAX),
      });
    }
  }
  for (const effect of timeline.sections.body.focus) {
    if (effect.mode !== "callout" || !effect.label?.trim()) continue;
    out.push({
      id: `callout:${effect.id}`,
      kind: "callout",
      english: effect.label,
      limit: textLimit(effect.label, language, LABEL_MAX),
    });
  }
  return out;
}

/**
 * Each voiced line as a subtitle source: its fitted timings, on the video's
 * clock from its start. The words as spoken, without any inline pause marker
 * the translation or an edit put in.
 */
export function timedLines(lines: readonly TimelineLanguageLine[]): TimedLine[] {
  return lines
    .filter((line) => line.clipKey && spokenText(line.text))
    .map((line) => ({
      text: spokenText(line.text),
      startAt: line.startAt,
      duration: line.clipSeconds ?? line.seconds,
      ...(line.alignment ? { alignment: line.alignment } : {}),
    }));
}

/**
 * The language's subtitles as caption cues, numbered `<code>-1`, `<code>-2`, …
 * Each carries its words' timings from the voice's own alignment, so the
 * highlight follows the translated voice as it follows the English; a cue too
 * long for the caption limits goes without, rather than with some of them.
 */
export function languageCues(lines: readonly TimelineLanguageLine[], code: string): CaptionCue[] {
  const time = (seconds: number): number => Math.max(0, Number(seconds.toFixed(3)));
  return cuesFromLines(timedLines(lines))
    .filter((cue) => cue.end > cue.start && cue.text.trim())
    .map((cue, index) => {
      const words =
        cue.words && cue.text.length <= 1000 && cue.words.length <= 200 && cue.words.every((w) => w.text.length <= 120)
          ? cue.words.map((word) => ({ start: time(word.start), end: time(word.end), text: word.text }))
          : undefined;
      return {
        id: `${code}-${index + 1}`,
        start: cue.start,
        end: cue.end,
        text: cue.text.slice(0, 1000),
        ...(words ? { words } : {}),
      };
    });
}

export function languageTimeline(
  timeline: Timeline,
  language: {
    code: string;
    depth: LanguageDepth;
    lines: readonly TimelineLanguageLine[];
    texts: readonly TranslatedText[];
    /**
     * The subtitles, when the server has made them already (the language
     * view's `cues`, timed word by word from the voice); else made from the
     * lines here, spread evenly over each clip.
     */
    cues?: readonly CaptionCue[];
  },
): Timeline {
  const end = timelineDuration(timeline);
  const voiced = new Map(
    language.lines.filter((line) => line.clipKey && spokenText(line.text)).map((line) => [line.id, line]),
  );
  const audio = timeline.audio.map((track) => {
    if (!VOICE_ROLES.has(track.role)) return track;
    const line = voiced.get(track.id);
    // English voice with no line in this language is silent, never English under the translation.
    if (!line || line.startAt >= end) return { ...track, enabled: false };
    return {
      ...track,
      assetKey: line.clipKey,
      startAt: line.startAt,
      duration: Math.max(0.05, line.clipSeconds ?? line.seconds),
      trimStart: 0,
      enabled: true,
    };
  });
  const captions = {
    ...timeline.captions,
    cues: language.cues ? [...language.cues] : languageCues(language.lines, language.code),
    // Arabic reads right to left whatever word its line starts with.
    direction: languageByCode(language.code)?.direction ?? "ltr",
  };
  if (language.depth !== "video") return { ...timeline, audio, captions };

  const textOf = new Map(language.texts.filter((t) => t.text.trim()).map((t) => [t.id, t.text]));
  const sections = { ...timeline.sections };
  for (const which of ["head", "end"] as const) {
    const scene = sections[which];
    if (!scene) continue;
    const content = scene.content;
    if (content.kind === "card") {
      sections[which] = {
        ...scene,
        content: {
          ...content,
          elements: content.elements.map((element) =>
            element.kind === "text" && textOf.has(`${which}:${element.id}`)
              ? { ...element, text: textOf.get(`${which}:${element.id}`)! }
              : element,
          ),
        },
      };
    } else if (content.kind === "text" && textOf.has(`${which}:text`)) {
      sections[which] = { ...scene, content: { ...content, text: textOf.get(`${which}:text`)! } };
    }
  }
  sections.body = {
    ...sections.body,
    focus: sections.body.focus.map((effect) =>
      effect.mode === "callout" && textOf.has(`callout:${effect.id}`)
        ? { ...effect, label: textOf.get(`callout:${effect.id}`)! }
        : effect,
    ),
  };
  return { ...timeline, audio, captions, sections };
}
